/**
 * Services hub — the landing page behind the "Services" nav link.
 *
 * Solves the IA problem where "Services" used to drop visitors directly
 * into /property-check and hid the rest of what Eqaya offers. This page
 * surfaces the two distinct service lines side-by-side, plus a quieter
 * row for the marketplaces (contractors, verifiers, project managers).
 *
 *   ┌──────────────────────────┐  ┌──────────────────────────┐
 *   │ Build or renovate         │  │ Property Check           │
 *   │ Long-form project flow    │  │ Quick verifier visit     │
 *   │  → Start a project        │  │  → Book Verification Work │
 *   └──────────────────────────┘  └──────────────────────────┘
 *      Browse contractors · Browse verifiers · Find a project manager
 *
 * Routed at /services in App.js. Brand tokens from theme/colors.js,
 * MUI sx prop (no Tailwind) per the project conventions.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Stack,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Divider,
} from '@mui/material';
import EngineeringIcon from '@mui/icons-material/Engineering';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import HandymanIcon from '@mui/icons-material/Handyman';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useAuth } from '../contexts/AuthContext';
import { colors } from '../theme/colors';

const PrimaryServiceCard = ({
  icon,
  eyebrow,
  title,
  body,
  bullets,
  ctaLabel,
  ctaTo,
  accent = 'primary',
  onClick,
}) => {
  const isAccent = accent === 'accent';
  const headerBg = isAccent ? colors.brand.gradientEarth : colors.brand.gradient;

  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 200ms cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 200ms',
        '&:hover': { transform: 'translateY(-4px)', boxShadow: 8 },
      }}
    >
      <Box
        sx={{
          background: headerBg,
          color: '#fff',
          px: { xs: 3, md: 4 },
          py: { xs: 4, md: 5 },
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
          <Chip
            label={eyebrow}
            size="small"
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              color: '#fff',
              fontWeight: 700,
              letterSpacing: '0.06em',
            }}
          />
        </Stack>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
            fontSize: { xs: '1.6rem', md: '2rem' },
          }}
        >
          {title}
        </Typography>
      </Box>

      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          px: { xs: 3, md: 4 },
          py: { xs: 3, md: 4 },
        }}
      >
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {body}
        </Typography>

        <Stack component="ul" spacing={1.25} sx={{ pl: 0, listStyle: 'none', mb: 4 }}>
          {bullets.map((b) => (
            <Stack
              component="li"
              key={b}
              direction="row"
              spacing={1.5}
              alignItems="flex-start"
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: isAccent ? colors.brand.secondary : colors.brand.primary,
                  mt: 1,
                  flexShrink: 0,
                }}
              />
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                {b}
              </Typography>
            </Stack>
          ))}
        </Stack>

        <Button
          variant="contained"
          size="large"
          endIcon={<ArrowForwardIcon />}
          onClick={onClick}
          sx={{
            alignSelf: 'flex-start',
            mt: 'auto',
            px: 3,
            py: 1.25,
            fontWeight: 700,
            textTransform: 'none',
            bgcolor: isAccent ? colors.brand.secondary : colors.brand.primary,
            '&:hover': {
              bgcolor: isAccent ? colors.brand.secondaryDark : colors.brand.primaryDark,
            },
          }}
        >
          {ctaLabel}
        </Button>
      </CardContent>
    </Card>
  );
};

const MarketplaceLink = ({ icon, title, body, onClick }) => (
  <Card
    variant="outlined"
    sx={{
      borderRadius: 2,
      height: '100%',
      transition: 'border-color 160ms ease, transform 160ms ease',
      '&:hover': { borderColor: colors.brand.primary, transform: 'translateY(-2px)' },
    }}
  >
    <CardActionArea onClick={onClick} sx={{ p: 2.5, height: '100%', alignItems: 'stretch' }}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box sx={{ color: colors.brand.primary, mt: 0.5 }}>{icon}</Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {body}
          </Typography>
        </Box>
      </Stack>
    </CardActionArea>
  </Card>
);

const ServicesHub = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Authed clients heading to "Start a project" go to their dashboard where
  // the New Project wizard lives. Unauthed visitors go straight to /login
  // (which offers both Sign In + Create Account) — the hub card itself
  // already does the marketing job with title + bullets, so an extra
  // /build-a-project landing in between would just be friction.
  // /build-a-project stays alive as a deep-link / SEO surface but isn't
  // linked from the in-app nav.
  const startProjectPath = isAuthenticated ? '/dashboard' : '/login';
  const propertyCheckPath = isAuthenticated ? '/property-check/dashboard' : '/property-check';

  return (
    <Box sx={{ bgcolor: colors.bg.page, minHeight: '100vh' }}>
      {/* Page header */}
      <Box sx={{ pt: { xs: 5, md: 8 }, pb: { xs: 2, md: 3 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="overline"
            sx={{ color: colors.brand.primary, fontWeight: 700, letterSpacing: '0.1em' }}
          >
            WHAT EQAYA DOES
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              mt: 1,
              mb: 1.5,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            }}
          >
            Two ways to use Eqaya.
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 400,
              color: 'text.secondary',
              maxWidth: 760,
              fontSize: { xs: '1rem', md: '1.15rem' },
              lineHeight: 1.6,
            }}
          >
            Whether you're building from the ground up or just need eyes on a property you
            already own, we connect you with vetted people on the ground in Zimbabwe — and
            keep your money protected until the work is done.
          </Typography>
        </Container>
      </Box>

      {/* Two primary service cards */}
      <Container maxWidth="lg" sx={{ pb: { xs: 5, md: 8 } }}>
        <Grid container spacing={{ xs: 3, md: 4 }}>
          <Grid item xs={12} md={6}>
            <PrimaryServiceCard
              icon={<EngineeringIcon sx={{ fontSize: 28, color: '#fff' }} />}
              eyebrow="BUILD / RENOVATE"
              title="Start or manage a construction project"
              body={
                'A guided end-to-end project flow: hire vetted contractors, sign binding contracts, '
                + 'fund work into an escrow wallet, and have every milestone independently verified before payment releases.'
              }
              bullets={[
                'AI-assisted scoping and budget estimates',
                'Multiple contractor bids you can compare side-by-side',
                'Independent verifiers visit and document every milestone',
                'Funds held in escrow — released only when you approve',
                'Real-time photo and video updates from site',
              ]}
              ctaLabel={isAuthenticated ? 'Go to your dashboard' : 'Start a project'}
              onClick={() => navigate(startProjectPath)}
              accent="primary"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <PrimaryServiceCard
              icon={<HomeWorkIcon sx={{ fontSize: 28, color: '#fff' }} />}
              eyebrow="ALREADY OWN A PROPERTY"
              title="Book Verification Work"
              body={
                'Send a verified person to your Zimbabwe property — house, Airbnb, rental — and get a '
                + 'photo-backed condition report within 48 hours. No project to set up.'
              }
              bullets={[
                'Five fixed-price packages from $25 to $75',
                'Lightweight signup — no rigorous onboarding',
                'Same vetted verifiers, same GPS-tagged evidence',
                'Built for diaspora owners, Airbnb hosts, absentee landlords',
                'Report delivered as a PDF by email and in-app',
              ]}
              ctaLabel="Book Verification Work"
              onClick={() => navigate(propertyCheckPath)}
              accent="accent"
            />
          </Grid>
        </Grid>
      </Container>

      {/* Marketplace row */}
      <Box
        sx={{
          bgcolor: colors.bg.subtle,
          borderTop: `1px solid ${colors.border.light}`,
          py: { xs: 5, md: 7 },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
              fontWeight: 700,
              letterSpacing: '0.08em',
              display: 'block',
              mb: 0.5,
            }}
          >
            OR BROWSE THE NETWORK
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, letterSpacing: '-0.01em' }}>
            Find a specific person directly
          </Typography>

          {/* "Find a project manager" card removed — feature ships in v2.
              Restore as a third <Grid item> here when project managers go live. */}
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6} md={6}>
              <MarketplaceLink
                icon={<HandymanIcon />}
                title="Browse contractors"
                body="Vetted construction contractors across Zimbabwe — profiles, portfolios, ratings."
                onClick={() => navigate('/contractors')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <MarketplaceLink
                icon={<VerifiedUserIcon />}
                title="Browse verifiers"
                body="The people who visit sites and document work. Independent, rated, on the ground."
                onClick={() => navigate('/verifiers')}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Footer CTA strip */}
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 }, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5 }}>
          Not sure which one fits?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 560, mx: 'auto' }}>
          If you're building something new or renovating, start a project. If you just need a
          report on a place you already own, book a Property Check.
        </Typography>
        <Divider sx={{ maxWidth: 60, mx: 'auto', borderColor: colors.brand.primary, borderBottomWidth: 3, mb: 3 }} />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
          <Button
            size="large"
            variant="contained"
            onClick={() => navigate(startProjectPath)}
            sx={{
              bgcolor: colors.brand.primary,
              fontWeight: 700,
              px: 3.5,
              textTransform: 'none',
              '&:hover': { bgcolor: colors.brand.primaryDark },
            }}
          >
            {isAuthenticated ? 'Go to dashboard' : 'Start a project'}
          </Button>
          <Button
            size="large"
            variant="outlined"
            onClick={() => navigate(propertyCheckPath)}
            sx={{
              borderColor: colors.brand.secondary,
              color: colors.brand.secondary,
              fontWeight: 700,
              px: 3.5,
              textTransform: 'none',
              '&:hover': {
                borderColor: colors.brand.secondaryDark,
                bgcolor: 'rgba(194, 65, 12, 0.04)',
              },
            }}
          >
            Book Verification Work
          </Button>
        </Stack>
      </Container>
    </Box>
  );
};

export default ServicesHub;
